import React from 'react';
import { observer } from 'mobx-react';
import { Page, Pane, PaneBody, PaneHeader } from '../../components/atoms/applications/page';
import './subscriptionSuspensionInfoPage.module.scss';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import { SubscriptionSuspensionInfoPageState } from './states/SubscriptionSuspensionInfoPageState';
import { withNavigation } from '../../components/hocs/withNavigation';
import { NavigationSpecs } from '../../states/NavigationState';
import { V3Routes } from '../../routes';
import { LoaderRow } from '../../components/atoms/layout/LoaderRow';
import { LabelPrimary } from '../../components/atoms/labels/LabelPrimary';
import { CheckBox } from '@sheinc/design-system';
import { NotFoundPage } from '../../../mainv2/containers/common/NotFoundPage';
import moment from 'moment-timezone';
import { BreadCrumb } from '../breadcrumb/BreadCrumb';
import { Button } from '@sheinc/shelikes-design-system';

type Props = {
  match: {
    params: {
      subscriptionCode: string;
    };
  };
};

export const SubscriptionSuspensionInfoPage = withNavigation(
  NavigationSpecs.side(),
  NavigationSpecs.none()
)(
  observer(
    class SubscriptionSuspensionInfoPage extends React.Component<Props> {
      pageState = new SubscriptionSuspensionInfoPageState();

      componentDidMount() {
        const { subscriptionCode } = this.props.match.params;
        this.pageState.init(subscriptionCode);
      }

      render() {
        const currentUser = GlobalStatesV3.currentUser;
        const { subscription: subscriptionResource, subscriptionCode, checked, setChecked } = this.pageState;
        if (!this.pageState.initialized) {
          return <LoaderRow />;
        }
        if (subscriptionResource.isNotFound()) {
          return <NotFoundPage entityType="Subscription" />;
        }
        if (subscriptionResource.isError()) {
          GlobalStatesV3.routing.push(V3Routes.home());
          return null;
        }
        const subscription = subscriptionResource.get();
        const breadCrumbItems = [{ text: '休会について' }];
        return (
          <Page id="subscription-suspension-info-page">
            <Pane styleName="pane">
              <PaneHeader title="休会について">
                <BreadCrumb items={breadCrumbItems} hasHome />
              </PaneHeader>
              <PaneBody>
                <div styleName="schedule_box">
                  <div styleName="schedule_box_left">
                    <div styleName="schedule_text">{currentUser.currentUser.name}さんの課金更新日</div>

                    {subscription.duration_month == 1 && (
                      <div styleName="schedule_date">
                        毎月<span styleName="date">{subscription.renewal_day}</span>日
                      </div>
                    )}
                    {subscription.duration_month > 1 && (
                      <div styleName="schedule_date">
                        <span styleName="date">{moment(subscription.next_payment_date).format('YYYY/MM/DD')}</span>
                      </div>
                    )}
                  </div>
                  <div styleName="schedule_box_right">
                    <div styleName="text">
                      {subscription.suspendable && (
                        <span styleName="blue">
                          ・{currentUser.currentUser.name}
                          さんは休会条件を満たしています。
                          <br />
                          ・休会は最短で{moment(subscription.next_suspendable_date).format('YYYY年MM月DD日')}
                          から可能となります。
                        </span>
                      )}
                      {!subscription.suspendable && subscription.duration_month == 1 && (
                        <span styleName="red">
                          あなたは現在会員継続6ヶ月未満のため、休会することができません。
                          <br />
                          <span styleName="light">
                            ※ {moment(subscription.next_suspendable_date).format('YYYY年MM月DD日')}
                            より休会申請をしていただけます。
                          </span>
                        </span>
                      )}
                      {!subscription.suspendable && subscription.duration_month != 1 && (
                        <span styleName="red">
                          あなたは
                          {subscription.duration_month === 1
                            ? '月額プラン'
                            : `${subscription.duration_month}ヶ月プラン`}
                          を契約中のため休会することができません。
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {subscription.suspendable && (
                  <div styleName="attention">
                    <div styleName="process">
                      <LabelPrimary label="休会方法" />
                      <div styleName="text">
                        ・月額プランでご契約中の場合、次ページの休会フォームへ必要事項を入力し、申請をお願いいたします。
                        <br />
                        ・6,12ヶ月プランでご契約中の場合、課金更新日の10日前の23:59までに
                        <a target="_blank" href="https://line.me/R/ti/p/%40xzy6836c">
                          SHElikes公式LINE
                        </a>
                        へ以下のご連絡をお願いいたします。
                        <br />
                        <br />
                        ▼ご連絡内容
                        <br />
                        - 6ヶ月・12ヶ月プラン満了後の休会手続き希望
                        <br />
                        - お名前
                        <br />
                        - メールアドレス
                        <br />
                        - ご希望の休会開始日
                        <br />- ご希望の休会終了日
                      </div>
                    </div>

                    <div>
                      <LabelPrimary label="休会条件" />
                      <div styleName="text">
                        下記の条件を両方満たしている方が対象となります。
                        <br />
                        <span styleName="schedule_text">
                          ・受講開始から<span styleName="red">6ヶ月以上</span>が経過している方
                        </span>
                        <br />
                        ※上記の条件に当てはまらない場合にも、条件に当てはまる場合に休会を承ることが可能な
                        <span styleName="schedule_text">特別休会制度</span>
                        もございます。詳細は
                        <a
                          target="_blank"
                          href="https://support.she-inc.jp/hc/ja/articles/360049445713--likes-%E4%BC%91%E4%BC%9A%E3%81%AF%E3%81%A7%E3%81%8D%E3%82%8B-"
                        >
                          こちら
                        </a>
                        よりご確認くださいませ。
                      </div>

                      <LabelPrimary label="休会期間" />
                      <div styleName="text">
                        ・休会は<span styleName="red">最大３ヶ月</span>することができます。
                        <br />
                        ※3ヶ月未満で休会から復帰した場合、再度休会いただくためには休会復帰日から6ヶ月の受講の継続が必要です
                      </div>

                      <LabelPrimary label="注意事項" />
                      <div styleName="attention_text">
                        ・休会を希望する課金更新日の<span styleName="red">【10日前】</span>
                        までに申請が必要です。それ以降になってしまった場合は、最短で休会できるのが次々回課金更新日以降となります。
                        <br />
                        ・自身で設定いただいた
                        <span styleName="red">休会復帰日に、自動的に休会前と同じプランで更新</span>
                        が行われます。一度復帰した場合、休会期間の延長や休会終了と同時の退会へのご変更は受け付けかねますのでご注意くださいませ。
                        <br />
                        ※休会復帰後のプラン変更をご希望の場合には、休会復帰日前の10日前までに
                        <a
                          target="_blank"
                          href="https://support.she-inc.jp/hc/ja/articles/1500002597221--likes-%E4%BC%91%E4%BC%9A%E4%BA%88%E7%B4%84%E4%B8%AD-%E4%BC%91%E4%BC%9A%E4%B8%AD%E3%81%AB%E4%BC%91%E4%BC%9A%E5%BE%A9%E5%B8%B0%E5%BE%8C%E3%81%AE%E3%83%97%E3%83%A9%E3%83%B3%E5%A4%89%E6%9B%B4%E3%82%92%E3%81%99%E3%82%8B%E6%96%B9%E6%B3%95%E3%81%AF-"
                        >
                          こちらのFAQ
                        </a>
                        を参考に、公式LINEまでお問い合わせください。
                        <br />
                        ・休会中はすべてのサービス利用ができません。
                        <br />
                        ・休会復帰日が、復帰後の新たな課金日になります。
                        <br />
                        ・一度休会した場合、再度6ヶ月受講を継続いただくことにより、再び休会することができます。
                        <br />
                        ・休会終了と同時の退会をご希望の場合、休会復帰日10日前までの退会のお手続きが必要となります。10日前を過ぎた場合にはお手続きを行っていただきかねます。
                        <br />
                        ・休会復帰後のプラン変更と休会終了と同時に退会するお手続きは【休会復帰日10日前】を過ぎた場合には承りかねますので、ご了承くださいませ。
                      </div>

                      <div styleName="attention_image" />

                      <div styleName="confirmation">
                        <CheckBox checked={checked} onCheck={() => setChecked()} id="suspension-confirmation-checkbox">
                          注意事項をよく読み理解しました
                        </CheckBox>
                      </div>
                    </div>
                  </div>
                )}

                <div styleName="attention">
                  {!subscription.suspendable && (
                    <div styleName="attention_text danger">休会条件を満たしていないため休会できません</div>
                  )}
                  <div styleName="submit_button">
                    <Button
                      id="submit-button"
                      disabled={!checked}
                      onClick={() => GlobalStatesV3.routing.push(V3Routes.subscriptionSuspend(subscriptionCode))}
                      fullWidth={GlobalStatesV3.viewport.isSp}
                    >
                      休会申請フォームに進む
                    </Button>
                  </div>
                </div>
              </PaneBody>
            </Pane>
          </Page>
        );
      }
    }
  )
);
